import React from 'react';
import { MapPin, Navigation, CalendarCheck, Users, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getLocalContent } from '@/lib/city-content';
import { useLocation } from '@/lib/locations';

export const NeighborhoodMap = () => {
  const location = useLocation();
  const content = getLocalContent(location.name, location.regionSlug);
  
  // Extract a few local areas or fallback to generic ones
  let neighborhoods = ['Downtown', 'Northside', 'Southside', 'West End', 'East Side', 'Suburbs'];
  const extracted = content.uniqueFaqs?.[1]?.answer?.match(/to ([a-zA-Z\s,]+) and beyond/)?.[1];
  if (extracted) {
    neighborhoods = extracted.split(',').map(s => s.trim()).filter(Boolean);
    if (neighborhoods.length < 4) {
      neighborhoods = [...neighborhoods, 'Downtown', 'Northside', 'Southside'].slice(0, 6);
    }
  }

  return (
    <Card className="overflow-hidden border-border shadow-sm h-full flex flex-col">
      <CardHeader className="bg-muted/30 border-b pb-4">
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl flex items-center gap-2">
            <MapPin className="text-primary w-5 h-5" />
            {location.name} Coverage
          </CardTitle>
          <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20 flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            Live Status
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-6 flex-1 flex flex-col">
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-muted/20 p-4 rounded-xl border border-border">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Users className="w-4 h-4" /> Active Teams
            </div>
            <div className="text-2xl font-bold text-foreground">
              {Math.floor(Math.random() * 5) + 3} <span className="text-sm font-normal text-muted-foreground">in area</span>
            </div>
          </div>
          <div className="bg-muted/20 p-4 rounded-xl border border-border">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <CalendarCheck className="w-4 h-4" /> Next Opening
            </div>
            <div className="text-2xl font-bold text-foreground">
              {new Date().getHours() < 14 ? (
                <div className="flex items-center gap-2">
                  <span className="text-green-600">Today</span>
                  <Badge className="bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20 text-xs">1 slot left</Badge>
                </div>
              ) : (
                "Tomorrow"
              )}
            </div>
          </div>
        </div>

        <div className="mb-3 text-sm font-bold text-muted-foreground tracking-wider">POPULAR NEIGHBORHOODS SERVED</div>
        <div className="grid grid-cols-2 gap-3 flex-1">
          {neighborhoods.slice(0, 6).map((hood, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate">{hood}</span>
            </div>
          ))}
        </div>
        
        <div className="mt-6 pt-4 border-t border-border flex justify-between items-center">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Navigation className="w-4 h-4" />
            Serving {location.name} & beyond
          </div>
          <Button size="sm" asChild>
            <a href="/book">Check Availability</a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
